import type { CSSProperties } from 'react';
import React, { useState } from 'react';
import ReactContentEditable from 'react-contenteditable';
import { CurrentSelectionStrategy } from '../steps/CardDesign/SelectionStrategy';
import type { EditorMode } from '../steps/CardDesign/useSurface';

interface ContentEditableProps {
  value: string;
  isEditing: boolean;
  /**
   * Passed down rather than read from the surface context on purpose: subscribing this component
   * to that context would re-render it on every hover, which is exactly the render noise the
   * `React.memo` below exists to keep away from the caret.
   */
  mode?: EditorMode;
  onChange?: (value: string) => void;
  /**
   * Fired when the caret lands in this field. In "Edit" mode every field is editable at
   * once, so focus — not a tap on the layer — is what tells the toolbar and the height measurer
   * which layer is being worked on.
   */
  onFocus?: () => void;
  style?: CSSProperties;
}

// `value` snapshot on init. This suspends 2-way binding.
// If we set ContentEditable.html to something other than what was previously typed,
// the browser will reset the caret position. Thus we want to prevent that loop.
const Root = (props: ContentEditableProps) => {
  const { value, isEditing, mode, onChange, onFocus, style } = props;
  const ref = CurrentSelectionStrategy.useInitTextCaret(isEditing, mode === 'edit');
  const [html, setHtml] = useState(`${value}\n`);

  return (
    <div
      // Same marker the non-editing branch of TextLayer carries, so `Surface`'s intro pulse still
      // finds this layer. It matters in "Edit" mode, where every editable field renders through
      // here from mount — without it the pulse would highlight only the layers you *cannot* type
      // into (URL, password, QR). Only in that mode: on the legacy path this component appears
      // just for the one layer being edited, which never carried the marker before.
      className={mode === 'edit' ? 'introable' : undefined}
      style={style}
      onFocus={onFocus}
    >
      <ReactContentEditable
        innerRef={ref}
        html={html}
        onChange={e => {
          setHtml(e.target.value);
          const innertText = ref.current?.innerText ?? '';
          onChange?.(innertText.replace(/\n$/, ''));
        }}
        style={
          {
            outline: 'none',
            // Block, not inline-block: the field must span the whole layer box. In "Edit" mode the
            // caret comes from where the user clicks, and an inline-block shrinks to its text -- to
            // 0px once the text is cleared, leaving nothing to click back into. The wrapper already
            // carries the width, alignment and alignment correction, so the glyphs don't move.
            display: 'block',
          } as CSSProperties
        }
      />
    </div>
  );
};

// Issue: The caret will intermittently jump to the end of the text when typing.
// Likely related to: https://github.com/lovasoa/react-contenteditable/issues/161
// Seems to repro when we have:
//   1) Text with "&". The way it encodes into an entity likely interferes with state comparison and therefore updates.
//   2) Frequent re-renders. In our case caused by mouse move events elsewhere in the react tree.
// The React.memo removes the render noise from #2.
// We can replace with a more precise solution when have time to look at `react-contenteditable` deeper.
export const ContentEditable = React.memo(Root, (prevProps, nextProps) => {
  // prettier-ignore
  return prevProps.value === nextProps.value
    && prevProps.isEditing === nextProps.isEditing
    && prevProps.mode === nextProps.mode
    // Not comparing `___props.onChange` / `___props.onFocus` as they are unstable.
    && JSON.stringify(prevProps.style) === JSON.stringify(nextProps.style);
});
